import { useEffect, useState } from "react";
import { toast } from "@/hooks/use-toast";

const STORAGE_KEY = "lumiplot.betaTicker.dismissedAt";
const EMAIL = "hi@lumiplot.ai";

export default function BetaTicker() {
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      setHidden(Boolean(localStorage.getItem(STORAGE_KEY)));
    } catch {
      setHidden(false);
    }
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {}
    setHidden(true);
  };

  const handleCopyEmail = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(EMAIL);
      toast({
        title: "Email copied!",
        description: `${EMAIL} has been copied to your clipboard.`,
      });
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Please try again or copy the email manually.",
        variant: "destructive",
      });
    }
  };

  if (hidden) return null;

  return (
    <div role="region" aria-label="Alpha announcement" className="fixed inset-x-0 top-0 z-50">
      <div className="relative h-8 w-full overflow-hidden border-b border-border bg-primary text-primary-foreground">
        {/* Scrolling track */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="beta-ticker-track h-full">
            <div className="beta-ticker-seg">
              <span className="opacity-90">Lumiplot is in alpha, thanks for supporting</span>
              <span aria-hidden="true">•</span>
              <button
                onClick={handleCopyEmail}
                className="underline underline-offset-2 hover:no-underline cursor-pointer bg-transparent border-none p-0 text-inherit font-inherit"
                type="button"
              >
                Give feedback
              </button>
              <span aria-hidden="true">•</span>
              <a
                href="https://blog.lumiplot.ai/blog/introducing-lumiplot-graphs-and-stats-made-easy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:no-underline cursor-pointer bg-transparent border-none p-0 text-inherit font-inherit"
              >
                Our story
              </a>
              <span aria-hidden="true">•</span>
              <span className="opacity-90">Founding offer: Sign up by May 30, 2026 to lock in 300 credits/month on Free for 12 months.</span>
            </div>
          </div>
        </div>

        {/* Dismiss */}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Hide announcement"
          className="absolute right-1.5 top-1.5 inline-flex h-5 w-5 items-center justify-center rounded hover:bg-primary-foreground/10 focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <svg width="14" height="14" viewBox="0 0 20 20" aria-hidden="true">
            <path fill="currentColor" d="M14.35 5.65a1 1 0 0 0-1.41-1.41L10 7.17 7.06 4.24A1 1 0 1 0 5.65 5.65L8.59 8.59l-2.94 2.94a1 1 0 1 0 1.41 1.41L10 10l2.94 2.94a1 1 0 1 0 1.41-1.41L11.41 8.59l2.94-2.94Z"/>
          </svg>
        </button>
      </div>

      {/* local styles for marquee */}
      <style>{`
        .beta-ticker-track {
          display: inline-flex;
          width: max-content;
          height: 100%;
          align-items: center;
          padding-left: 100vw; /* start off-screen at the right edge */
          animation: beta-ticker-scroll 40s linear infinite;
        }
        .beta-ticker-seg {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 0.8rem;   /* small, thin bar */
          line-height: 2rem;   /* matches h-8 */
          white-space: nowrap;
        }
        @keyframes beta-ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-100%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .beta-ticker-track { animation: none; }
        }
      `}</style>
    </div>
  );
}


